import { deflate, inflate } from "pako";
import { serializeAsJSON } from "../../packages/excalidraw/data/json";
import { ImportedDataState } from "../../packages/excalidraw/data/types";
import { isInitializedImageElement } from "../../packages/excalidraw/element/typeChecks";
import {
  ExcalidrawElement,
  FileId,
} from "../../packages/excalidraw/element/types";
import {
  AppState,
  BinaryFileData,
  BinaryFiles,
} from "../../packages/excalidraw/types";
import { FILE_UPLOAD_MAX_BYTES } from "../app_constants";
import { encodeFilesForUpload, isFileTooBigError } from "./FileManager";
import { saveFilesToR2, loadFilesFromR2 } from "./r2AWSStorage";
import type { SaveFailureKind } from "./saveFailure";

/**
 * Cloud scene IO for "My Files" — save/load a named cloud file's scene.
 *
 * Unlike the legacy snapshot share (`r2AWSStorage.ts`), cloud scenes are NOT
 * encrypted: access control lives in the scene endpoint (owner/visibility
 * checks) rather than in a key embedded in the URL. Compression stays.
 *
 * Image assets reuse the existing R2 asset mechanism, keyed under a per-file
 * `files/<fileId>/` prefix. They are obfuscated (compressed) with a fixed,
 * non-secret key — that key is NOT the access boundary. Access control lives at
 * the `/api/assets` endpoint, which applies the same owner/visibility rule as
 * the scene endpoint: writes are owner-only, and a private file's assets are
 * served only to its owner (bearer token), never at a guessable bucket URL.
 */

const SERVER_URL = import.meta.env.VITE_APP_BACKEND_URL || "/api";
const SCENE_BACKEND_URL = `${SERVER_URL}/scene`;

// Fixed all-zero AES key (see module header: NOT the access boundary). Must
// stay constant — changing it makes every already-saved asset undecodable.
const CLOUD_ASSET_KEY = "AAAAAAAAAAAAAAAAAAAAAA";

const filesPrefix = (fileId: string) => `/files/${fileId}`;

interface CFFunctionsResponse<T> {
  data: T | null;
  error: { message: string } | null;
}

export interface SaveSceneResult {
  version: number | null;
  errorMessage: string | null;
  /**
   * How the save failed, so callers can tell a genuine connectivity problem
   * from a server that answered with an error (a 401/404/500 must never be
   * reported to the user as "offline"). `null` on success.
   */
  errorKind: SaveFailureKind | null;
  /**
   * Set when the scene DID save but one or more image assets were permanently
   * skipped (too large). The save is a success — retrying cannot help — so the
   * only honest thing left is to say what was dropped. `null` otherwise.
   */
  assetWarning: string | null;
  /**
   * Set when the server refused the save because someone else committed first
   * (409). Distinct from `errorMessage` because it is not a failure to retry:
   * the same bytes on the same base version can only be refused again. `current`
   * is the version that won, which is what any recovery has to build on.
   */
  stale: { current: number } | null;
  /** SHA-256 the server recorded for the committed bytes. `null` unless saved. */
  contentHash: string | null;
}

/**
 * The version a freshly-created row starts at (`files.version` DEFAULT 1), and
 * therefore the base version the first PUT to a new file must declare.
 */
export const CLOUD_FILE_INITIAL_VERSION = 1;

const failure = (
  errorMessage: string,
  errorKind: SaveFailureKind,
): SaveSceneResult => ({
  version: null,
  errorMessage,
  errorKind,
  assetWarning: null,
  stale: null,
  contentHash: null,
});

/**
 * An asset too large to ever upload. Phrased in source-image terms (~2 MB):
 * `FILE_UPLOAD_MAX_BYTES` (3 MiB) caps the base64 dataURL, which is ~4/3 the
 * size of the image itself.
 */
export const OVERSIZED_ASSET_WARNING =
  "One or more images are too large to save to the cloud (max ~2 MB each) and were skipped.";

/** An asset upload that may well succeed next time (404/503/offline/unknown). */
export const TRANSIENT_ASSET_FAILURE = "Some images failed to upload.";

/**
 * Which of a cloud file's image assets are already on the server, so autosave
 * stops re-uploading the whole scene every couple of seconds. Asset ids are
 * content-addressed (id = hash of the bytes), so a known id is known bytes.
 *
 * Keyed by cloud fileId because assets live under a `files/<fileId>/` prefix:
 * "save a copy" writes under a NEW id and must upload everything again.
 *
 * Session-scoped and deliberately not persisted — `/api/assets` has no DELETE
 * and the buckets have no lifecycle rules, so within a session a stored asset
 * stays stored; across sessions we would rather re-upload than trust stale state.
 */
interface FileAssetState {
  /** Proven server-side: ids `saveFilesToR2` reported in `savedFiles`. */
  persisted: Set<FileId>;
  /** Too large to ever store (413 / the encoder's size throw). */
  permanentlyRejected: Set<FileId>;
}

const assetStateByFile = new Map<string, FileAssetState>();

const assetStateFor = (fileId: string): FileAssetState => {
  let state = assetStateByFile.get(fileId);
  if (!state) {
    state = { persisted: new Set(), permanentlyRejected: new Set() };
    assetStateByFile.set(fileId, state);
  }
  return state;
};

/** Drop the upload cache — for one file, or all of them (e.g. on logout). */
export const forgetPersistedAssets = (fileId?: string) => {
  if (fileId === undefined) {
    assetStateByFile.clear();
  } else {
    assetStateByFile.delete(fileId);
  }
};

/** What the caller should do after attempting the scene's asset uploads. */
interface AssetUploadOutcome {
  /**
   * Non-null when the scene PUT must NOT run: the failure is transient, so the
   * last consistent server copy is left alone and the save is retried.
   */
  abort: SaveSceneResult | null;
  /** Assets permanently skipped; the PUT proceeds and the user is told. */
  warning: string | null;
}

/**
 * Upload the image assets referenced by a scene, under the file's own prefix.
 *
 * Image failures used to be discarded (`saveFilesToR2` never throws, and its
 * `erroredFiles` was ignored), so a save reported success while the images were
 * missing. Then every failure aborted the save — which turned a PERMANENT
 * rejection (an asset too large to ever fit) into a save that could never
 * succeed again for the whole file, retried forever behind a generic chip.
 *
 * So failures are classified:
 *  - PERMANENT (the encoder's `FileTooBigError`, or HTTP 413): retrying is
 *    pointless. Skip that asset, let the scene PUT proceed, and return a warning.
 *    The server copy stays consistent with whichever assets DID land; the skipped
 *    ones render as not-found — visibly honest, unlike a file that silently stops
 *    saving.
 *  - TRANSIENT (404/503, `fetch` threw, anything else): abort before the PUT and
 *    retry. The scene must not reference an asset that may yet appear.
 *
 * Mixed permanent + transient is treated as TRANSIENT (abort + retry): the
 * transient asset may still land, and publishing the scene now would freeze the
 * "images missing" state into the server copy. Once the transient failure clears,
 * the very next retry sees permanent-only and saves with the warning — so this
 * defers the save, it never blocks it.
 *
 * Encoding is per file, not per batch: `encodeFilesForUpload` throws on the FIRST
 * oversized file, which would otherwise take every other asset in the scene down
 * with it.
 *
 * Both the encode and the POST are skipped for assets `assetStateByFile` already
 * knows about — every save used to re-send the whole scene's images, and autosave
 * saves every couple of seconds. A permanently rejected asset is skipped but
 * still raises the warning while a live element references it: the image really
 * is missing from the cloud copy, whether or not this save tried again.
 *
 * A transient failure is `"server"`, never `"offline"`: with a mix of causes
 * behind it we cannot prove connectivity (see data/saveFailure.ts, which still
 * detects a genuinely offline browser via `navigator.onLine`).
 */
const uploadSceneImages = async (
  fileId: string,
  elements: readonly ExcalidrawElement[],
  files: BinaryFiles,
  accessToken: string,
): Promise<AssetUploadOutcome> => {
  const filesMap = new Map<FileId, BinaryFileData>();
  // Assets of DELETED elements are still uploaded (an undo must not leave the
  // scene pointing at a blob we chose to skip) but must not raise the warning:
  // a permanently rejected image the user has since deleted was otherwise
  // re-reported on every save, forever.
  const liveFileIds = new Set<FileId>();
  for (const element of elements) {
    if (isInitializedImageElement(element) && files[element.fileId]) {
      filesMap.set(element.fileId, files[element.fileId]);
      if (!element.isDeleted) {
        liveFileIds.add(element.fileId);
      }
    }
  }
  if (!filesMap.size) {
    return { abort: null, warning: null };
  }

  const state = assetStateFor(fileId);
  let hasPermanentFailure = false;
  let hasTransientFailure = false;

  const filesToUpload: { id: FileId; buffer: Uint8Array }[] = [];
  for (const [id, fileData] of filesMap) {
    if (state.persisted.has(id)) {
      continue;
    }
    if (state.permanentlyRejected.has(id)) {
      // Still on the canvas, so the user still needs telling.
      hasPermanentFailure ||= liveFileIds.has(id);
      continue;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const encoded = await encodeFilesForUpload({
        files: new Map([[id, fileData]]),
        encryptionKey: CLOUD_ASSET_KEY,
        maxBytes: FILE_UPLOAD_MAX_BYTES,
      });
      filesToUpload.push(...encoded);
    } catch (error: any) {
      console.error(error);
      if (isFileTooBigError(error)) {
        state.permanentlyRejected.add(id);
        hasPermanentFailure ||= liveFileIds.has(id);
      } else {
        // Anything else (no `crypto.subtle` on a non-secure origin, an
        // allocation failure) may well encode next time — blacklisting it
        // published a scene referencing an asset that was never stored.
        hasTransientFailure = true;
      }
    }
  }

  if (filesToUpload.length) {
    const { savedFiles, erroredFiles, erroredStatuses } = await saveFilesToR2({
      prefix: filesPrefix(fileId),
      files: filesToUpload,
      token: accessToken,
    });
    // Only `savedFiles` proves the asset is stored (see `saveFilesToR2`). A
    // transient failure must stay uploadable, or the retry that should fix it
    // would skip the very asset that is missing.
    for (const id of savedFiles.keys()) {
      state.persisted.add(id);
    }
    for (const id of erroredFiles.keys()) {
      if (erroredStatuses?.get(id) === 413) {
        state.permanentlyRejected.add(id);
        hasPermanentFailure ||= liveFileIds.has(id);
      } else {
        // 404 stays transient: usually a token-refresh race the next retry
        // clears, and the scene PUT (same auth) would fail identically anyway.
        hasTransientFailure = true;
      }
    }
  }

  if (hasTransientFailure) {
    return {
      abort: failure(TRANSIENT_ASSET_FAILURE, "server"),
      warning: null,
    };
  }

  return {
    abort: null,
    warning: hasPermanentFailure ? OVERSIZED_ASSET_WARNING : null,
  };
};

/**
 * Upload a scene's image assets, then compress and PUT the scene to the
 * authorized cloud endpoint. Returns the new server-side version on success.
 *
 * Assets go FIRST, and a TRANSIENTLY failed asset aborts before the PUT. The
 * server copy must never reference an asset that may still land: leaving the last
 * consistent scene in place (while the caller's write-through buffer holds the
 * newest work and retries) beats publishing a scene with broken images. The
 * reverse order can only trade that for orphaned asset objects, which are inert.
 * A PERMANENTLY rejected asset does NOT abort — see `uploadSceneImages`: the
 * scene saves and the result carries `assetWarning`.
 *
 * Failures are classified: only a request that never reached a server (fetch
 * threw) is `"network"`. Anything the server answered — including "no token",
 * which never leaves the client — is `"server"`, i.e. a real error.
 */
export const saveSceneToCloud = async (
  fileId: string,
  elements: readonly ExcalidrawElement[],
  appState: Partial<AppState>,
  files: BinaryFiles,
  accessToken: string,
  /** Sent as `If-Match`; null omits it and the server answers 428. */
  baseVersion: number | null,
): Promise<SaveSceneResult> => {
  if (!accessToken) {
    return failure("Login to continue", "server");
  }

  // Before the scene PUT — see the ordering rationale above.
  const { abort, warning: assetWarning } = await uploadSceneImages(
    fileId,
    elements,
    files,
    accessToken,
  );
  if (abort) {
    return abort;
  }

  const payload = deflate(
    new TextEncoder().encode(
      serializeAsJSON(elements, appState, files, "database"),
    ),
  );

  let response: Response;
  try {
    response = await fetch(
      `${SCENE_BACKEND_URL}/${encodeURIComponent(fileId)}`,
      {
        method: "PUT",
        body: payload,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/octet-stream",
          ...(baseVersion == null ? null : { "If-Match": String(baseVersion) }),
        },
      },
    );
  } catch (error: any) {
    // `fetch` only rejects below the HTTP layer: offline, DNS, connection
    // refused, CORS preflight blocked, aborted. No server ever answered.
    console.error(error);
    return failure("Could not reach the server.", "network");
  }

  // From here on the server DID answer — any failure is a real error, never
  // "offline".
  try {
    const json: CFFunctionsResponse<{
      id?: string;
      version?: number;
      content_hash?: string;
      error?: string;
      current?: number;
    }> = await response.json();

    // Someone else committed first; not a retryable error (see `stale`).
    if (response.status === 409 && json.data?.error === "stale") {
      return {
        version: null,
        errorMessage: null,
        errorKind: null,
        assetWarning: null,
        stale: { current: json.data.current as number },
        contentHash: null,
      };
    }

    if (!response.ok || json.error) {
      return failure(
        json.error?.message ||
          `Could not save to the cloud (HTTP ${response.status}).`,
        "server",
      );
    }

    return {
      version: json.data?.version ?? null,
      errorMessage: null,
      errorKind: null,
      assetWarning,
      stale: null,
      contentHash: json.data?.content_hash ?? null,
    };
  } catch (error: any) {
    // A malformed response body — images are handled before the PUT and never
    // reach here. The PUT itself was answered, so this is not a connectivity
    // claim we can make; if the user really is offline, the status classifier
    // sees that from `navigator.onLine` instead.
    console.error(error);
    return failure("Could not save to the cloud.", "server");
  }
};

export interface LoadSceneResult {
  data: ImportedDataState | null;
  status: number;
}

/**
 * GET + decompress a cloud scene. `status` mirrors the HTTP status so callers
 * can distinguish a 404 (no access / missing) from a transport error.
 */
export const loadSceneFromCloud = async (
  fileId: string,
  accessToken?: string,
): Promise<LoadSceneResult> => {
  try {
    const response = await fetch(
      `${SCENE_BACKEND_URL}/${encodeURIComponent(fileId)}`,
      {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      },
    );

    // 204: the row exists and the caller is authorized, but no scene blob was
    // ever written (e.g. a first-save whose PUT failed). Surface it as an empty
    // scene so the editor self-heals into an empty canvas the user can save
    // into — NOT a decompress attempt on an empty body (which would throw and
    // masquerade as a transport error).
    if (response.status === 204) {
      return {
        data: { elements: [], appState: null, files: undefined },
        status: 204,
      };
    }

    if (!response.ok) {
      return { data: null, status: response.status };
    }

    const buffer = await response.arrayBuffer();
    const json = new TextDecoder().decode(inflate(new Uint8Array(buffer)));
    const data: ImportedDataState = JSON.parse(json);

    return {
      data: {
        elements: data.elements || null,
        appState: data.appState || null,
        files: data.files,
      },
      status: response.status,
    };
  } catch (error: any) {
    console.error(error);
    return { data: null, status: 0 };
  }
};

/**
 * Fetch the image assets for a cloud file, given the file ids referenced by its
 * elements. Reads go through the `/api/assets` endpoint (owner/visibility gate),
 * not straight to the bucket.
 *
 * `accessToken` is required to read a PRIVATE file's assets (the owner's bearer
 * token); a public file needs none. Callers loading their OWN file must pass it,
 * or the endpoint returns 404 for the private-file assets.
 */
export const loadSceneImagesFromCloud = async (
  fileId: string,
  fileIds: readonly FileId[],
  accessToken?: string,
) => {
  const result = await loadFilesFromR2(
    filesPrefix(fileId),
    CLOUD_ASSET_KEY,
    fileIds,
    accessToken,
  );

  // An asset we just downloaded is, by definition, already stored — without
  // this the first save after opening a file re-uploads the entire scene.
  const state = assetStateFor(fileId);
  for (const file of result.loadedFiles) {
    state.persisted.add(file.id);
  }

  return result;
};

/** Cloud-image loads that failed, and the token they failed under. */
export interface PendingCloudImages {
  fileId: string;
  fileIds: readonly FileId[];
  /** Access token in use when these loads failed (`undefined` if there was none). */
  token: string | undefined;
}

/**
 * May a failed cloud-image load be re-attempted?
 *
 * The common cause of a private file's assets 404ing is that the load ran before
 * (or after) a usable token existed — and the hydration effect deliberately never
 * re-runs for an already-hydrated file, so nothing else would ever try again and
 * the images stayed broken for the whole session. A retry is only worth making
 * when the token has actually CHANGED (otherwise the same request, same answer),
 * and only for the file still on screen.
 */
export const shouldRetryCloudImages = (
  pending: PendingCloudImages | null,
  currentFileId: string | null,
  currentToken: string | undefined,
): boolean =>
  !!pending &&
  pending.fileIds.length > 0 &&
  pending.fileId === currentFileId &&
  !!currentToken &&
  currentToken !== pending.token;
